import {
    compactStack,
    requestPath,
    scrubSecrets,
    type ISupportConsoleEntry,
    type ISupportIssue,
    type IUserContextError,
    type SupportConsoleLevel,
} from '@srm/screen-sharing/light';
import { createEmitter } from '@srm/ui/lib/valueStore';

/** Сколько последних ошибок видит поддержка в контексте показа экрана. */
export const SUPPORT_ERROR_LIMIT = 10;
const TEXT_LIMIT = 300;

const errors: IUserContextError[] = [];

/** Ошибки страницы со стеком для пакета диагностики (§43.2) — столько последних. */
export const SUPPORT_CONSOLE_LIMIT = 30;
const consoleEntries: ISupportConsoleEntry[] = [];

/** Сообщение сервера из необработанного промиса — не длиннее: там бывают данные документа. */
export const REJECTION_TEXT_LIMIT = 200;

const cut = (text: string, limit: number) => (text.length > limit ? `${text.slice(0, limit - 1)}…` : text);

/**
 * Последние ошибки запросов и консоли для сотрудника поддержки (`IUserContext.errors`): только тексты, без тел
 * запросов; токены, ключи и непрозрачные идентификаторы маскирует общий `scrubSecrets` пакета. `traceId` — код
 * обращения запроса — дописывается как есть: по нему поддержка ищет запись в журнале. Пишут перехватчик ответов
 * `@/http` и `installSupportErrorCapture`.
 */
export function recordSupportError(text: string, traceId?: string): void {
    const clean = scrubSecrets(text.replace(/\s+/g, ' ').trim());
    if (!clean) return;
    const code = traceId ? ` · код ${traceId.slice(0, 64)}` : '';
    errors.push({ at: new Date().toISOString(), text: cut(clean, TEXT_LIMIT - code.length) + code });
    if (errors.length > SUPPORT_ERROR_LIMIT) errors.splice(0, errors.length - SUPPORT_ERROR_LIMIT);
}

/** Последние ошибки; `since` (мс) — только записанные не раньше: поддержке не нужна вся история вкладки. */
export const recentSupportErrors = (since?: number): IUserContextError[] =>
    since === undefined ? errors.slice() : errors.filter((entry) => Date.parse(entry.at) >= since);

/** Ошибки страницы со стеком (≤ 30) — раздел `console` пакета диагностики. */
export const recentSupportConsole = (): ISupportConsoleEntry[] => consoleEntries.slice();

/**
 * Ошибка страницы: текст — в контекст показа (без стека), запись со стеком — в журнал для пакета диагностики. Стек — без
 * адресов бандла, секреты замаскированы.
 */
export function recordSupportConsoleError(level: SupportConsoleLevel, text: string, stack?: unknown): void {
    recordSupportError(text);
    const clean = scrubSecrets(text.replace(/\s+/g, ' ').trim());
    if (!clean) return;
    const trace = compactStack(stack);
    consoleEntries.push({
        at: new Date().toISOString(),
        level,
        text: cut(clean, TEXT_LIMIT),
        ...(trace ? { stack: trace } : {}),
    });
    if (consoleEntries.length > SUPPORT_CONSOLE_LIMIT)
        consoleEntries.splice(0, consoleEntries.length - SUPPORT_CONSOLE_LIMIT);
}

/** Выход и смена пользователя: ошибки прежней учётки следующему не достаются. */
export const clearSupportErrors = () => {
    errors.length = 0;
    consoleEntries.length = 0;
};

const issues = createEmitter<ISupportIssue>();

/** Автоотметки ошибок (§44.2): показ этой вкладки подписывается на время показа. Возвращает отписку. */
export const onSupportIssue = (listener: (issue: ISupportIssue) => void): (() => void) => issues.subscribe(listener);

/** Отметка ошибки — тем, кто подписан (идущему показу); без показа — никому. */
export const reportSupportIssue = (issue: ISupportIssue): void => issues.emit(issue);

/**
 * Какие ответы становятся отметкой в журнале показа (§44.2): 5xx и отказы сети — всегда; 4xx — кроме 401 (обновление
 * токена) и 404 на чтение (справочные запросы: «нет такой записи» — обычный ответ); фоновые запросы
 * (`skipSupportErrorLog`) — никогда. `null` — не отметка.
 */
export function supportIssueOfRequest(
    method: string | undefined,
    url: string | undefined,
    status: number | undefined,
    traceId?: string,
    text = describeRequestError(method, url, status),
): ISupportIssue | null {
    const verb = (method ?? 'get').toUpperCase();
    if (status !== undefined) {
        if (status < 400 || status === 401) return null;
        if (status === 404 && verb === 'GET') return null;
    }
    const path = requestPath(url);
    return {
        at: new Date().toISOString(),
        kind: status === undefined ? 'network' : 'http',
        method: verb,
        ...(path ? { path } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(traceId ? { traceId } : {}),
        text,
    };
}

/** Адрес запроса без параметров: в строке запроса бывают поисковые фразы и ключи. */
export function describeRequestError(method: string | undefined, url: string | undefined, status: number | undefined) {
    const path = (url ?? '').split(/[?#]/)[0];
    return `${(method ?? 'get').toUpperCase()} ${path || '/'} — ${status ? `ответ ${status}` : 'нет ответа'}`;
}

const reasonText = (reason: unknown) =>
    reason instanceof Error ? `${reason.name}: ${reason.message}` : typeof reason === 'string' ? reason : '';
const stackOf = (reason: unknown) => (reason instanceof Error ? reason.stack : undefined);

/** Ошибка страницы — в журнал и, во время показа, отметкой специалистам. */
function pageError(level: 'error' | 'unhandled', text: string, stack: unknown): void {
    recordSupportConsoleError(level, text, stack);
    reportSupportIssue({ at: new Date().toISOString(), kind: level === 'error' ? 'console' : 'unhandled', text });
}

/**
 * Безвредный шум `window.onerror`, а не ошибки страницы: «ResizeObserver loop…» Chrome шлёт, когда наблюдатель (гриды,
 * оболочка) не успел за кадр; «Script error.» без объекта ошибки — скрипт с чужого источника, ни текста, ни стека. В
 * журнал и в отметки они не идут: склеиваясь с настоящими ошибками (§44.2), они вытесняли бы их текст.
 */
export const isSupportErrorNoise = (event: Pick<ErrorEvent, 'message' | 'error'>): boolean =>
    /^ResizeObserver loop\b/.test(event.message ?? '') ||
    (!event.error && /^Script error\.?$/.test(event.message ?? ''));

let installed = 0;
let detach: (() => void) | undefined;

/**
 * Ошибки консоли страницы (`window.onerror`, необработанные промисы): в контекст показа, со стеком — в журнал для пакета
 * диагностики, во время показа — отметкой специалистам. Возвращает отписку.
 */
export function installSupportErrorCapture(): () => void {
    if (installed++ === 0) {
        const onError = (event: ErrorEvent) => {
            if (isSupportErrorNoise(event)) return;
            pageError(
                'error',
                (event.error ? reasonText(event.error) : '') || event.message || 'Ошибка скрипта',
                stackOf(event.error),
            );
        };
        const onRejection = (event: PromiseRejectionEvent) => {
            const text = reasonText(event.reason);
            if (text)
                pageError(
                    'unhandled',
                    `Необработанная ошибка: ${cut(text, REJECTION_TEXT_LIMIT)}`,
                    stackOf(event.reason),
                );
        };
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onRejection);
        detach = () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
        };
    }
    let attached = true;
    return () => {
        if (!attached) return;
        attached = false;
        if (--installed === 0) detach?.();
    };
}
